import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { Component } from "react";
import { AnnotationText } from "../annotation";
import { DocumentFeedback, initAnnotations } from "../annotation-api";

const knownTableSections = [
  "Orders",
  "Data Interpretation",
  "Disposition",
  "Results",
  "Diagnosis/Impression",
  "Medications",
  "Discharge Instructions",
  "Procedures",
  "Attestations",
  "Past Surgical History",
  "Allergies",
  "Family History",
] as const;
type KnownTableSections = (typeof knownTableSections)[number];

type Section = {
  headers: string[];
  rows: object[];
  name?: string;
};

function hasArrayValue(a: any[]) {
  if (typeof a[0] === "object") {
    const e = Object.entries(a[0]);
    return e[0][1] && Array.isArray(e[0][1]);
  }
  return false;
}
function extractKVSections(a: object) {
  let sections: Section[] = [];
  try {
    if (Array.isArray(a)) {
      const nested = hasArrayValue(a);
      if (nested) {
        a.forEach((v) => {
          const values = Object.entries(v);
          if (typeof values[0][1] === "string") {
            const headers = Object.keys(v);
            sections.push({ name: "", headers, rows: [v] });
          } else {
            values.forEach(([name, value]) => {
              if (Array.isArray(value) && value.length) {
                sections.push({
                  name,
                  headers: Object.keys(value[0]),
                  rows: value,
                });
              }
            });
          }
        });
      } else {
        const isValue = typeof a[0] === "string";
        if (isValue) {
          sections.push({ name: "", headers: [], rows: a });
        } else {
          const headers = Object.keys(a[0]);
          sections.push({ name: "", headers, rows: a });
        }
      }
    } else {
      const values = Object.entries(a);
      values.forEach(([name, value]) => {
        if (Array.isArray(value) && value.length) {
          sections.push({ name, headers: Object.keys(value[0]), rows: value });
        }
      });
    }
  } catch (e) {
    console.info("error", e);
  }
  return sections;
}

function extractValue(a: any, key: string) {
  let v = a || "";

  if (typeof a === "string") {
    return a;
  }

  if (Array.isArray(a)) {
    if (a.length == 1) {
      v = a[0];
    }
  }
  if (v[key]) {
    return v[key];
  } else if (typeof a === "string") {
    return v;
  } else if (typeof v === "object") {
    const values = Object.values(v);
    return values[0] || v;
  }
  return v;
}

function extractDataInterpretation(obj: object) {
  let sections: Section[] = [];
  const isList = Array.isArray(obj);

  function processObj(kv: object) {
    const values = Object.entries(kv);
    values.forEach(([name, value]) => {
      
      if (Array.isArray(value)) {
 
        const str = value.map((v) => extractValue(v, "Result"));
      
        //@ts-ignore cast
        let sp = str.map((s) => (Array.isArray(s) ? s.split(")") : [s]));
        sp.forEach((s) => {
          for (let i = 0; i < s.length - 1; i++) {
            s[i] += ")";
          }
        });

        const rows = sp.map((a) => ({ name: a[0], values: a.slice(1) }));
        sections.push({ name, headers: [name], rows });
      } else {
   
        sections.push({
          name: "",
          headers: [],
          rows: [{ name: "", values: [value] }],
        });
      }
    });
  }

  try {
    if (isList) {
      const isResult = obj[0] && obj[0].Result;
      if (isResult) {
        const rows = obj.map((v) => ({
          name: null,
          values: [extractValue(v, "Result")],
        }));
        sections.push({ headers: [], rows });
      } else {
        obj.forEach((o) => processObj(o));
      }
    } else {
      processObj(obj);
    }
  } catch (e) {
    console.info("error", e);
  }

  return sections;
}

class ErrorBoundary extends Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.info(
      "Superchart table errorBoundary caught an error: ",
      error,
      errorInfo
    );
    this.setState({ hasError: true });
  }

  render() {
    if (this.state.hasError) {
      return <h1>Error parsing super chart table</h1>;
    }

    return this.props.children;
  }
}

export function santitizeContent(raw: string | string[] | object): string {
  if (typeof raw === "object") {
    return "";
  }
  try {
    //@ts-ignore cas
    let content = Array.isArray(raw)
      ? raw.join("")
      : (Array.isArray(raw) ? raw.split("\\n") : []).join("");
    content = content.replace(/\//g, "");
    return content;
  } catch (e) {
    return "";
  }
}

export function SectionTable(
  raw: string | object,
  section: string | KnownTableSections,
  bundleKey: string,
  feedback: DocumentFeedback[],
  onSave: () => void
) {
  let json: any;
  let rows: any[];
  let content: string;

  if (typeof raw === "string") {
    content = santitizeContent(raw);
    if (knownTableSections.includes(section as KnownTableSections)) {
      try {
        json = JSON.parse(content);
      } catch (e) {
        return "PARSE ERROR: " + e + " " + content;
      }
    }
  } else {
    json = raw;
  }

  if (
    section === "Orders" ||
    section === "Results" ||
    section === "Diagnosis/Impression" ||
    section === "Medications" ||
    section === "Discharge Instructions" ||
    section === "Procedures" ||
    section === "Attestations" ||
    section === "Past Surgical History" ||
    section === "Allergies" ||
    section === "Family History"
  ) {
    rows = extractKVSections(json);
  }

  if (section === "Data Interpretation") {
    rows = extractDataInterpretation(json);
  }

  function sectionComponent() {
    switch (section) {
      case "Data Interpretation":
        return (
          <ErrorBoundary>
            {rows.map((s: Section, i) => (
              <>
                {s.name ? (
                  <p style={{ color: "#666" }}>
                    <b>
                      <AnnotationText
                        api={initAnnotations(
                          (s.name || "").toString(),
                          section,
                          bundleKey,
                          feedback,
                          onSave
                        )}
                        tag={section}
                      />
                    </b>
                  </p>
                ) : (
                  <br />
                )}
                {s.rows.map((r: any) => (
                  <>
                    {r.name && (
                      <>
                        <AnnotationText
                          api={initAnnotations(
                            (r.name || "").toString(),
                            section,
                            bundleKey,
                            feedback,
                            onSave
                          )}
                          tag={section}
                        />{" "}
                        <br />
                      </>
                    )}

                    {r.values.map((v) => (
                      <>
                        <AnnotationText
                          api={initAnnotations(
                            (v || "").toString(),
                            section,
                            bundleKey,
                            feedback,
                            onSave
                          )}
                          tag={section}
                        />
                        <br />
                      </>
                    ))}
                  </>
                ))}
              </>
            ))}
          </ErrorBoundary>
        );
      case "Diagnosis/Impression":
      case "Results":
      case "Orders":
      case "Medications":
      case "Discharge Instructions":
      case "Procedures":
      case "Attestations":
      case "Past Surgical History":
      case "Allergies":
      case "Family History":
        return (
          <ErrorBoundary>
            {rows.map((s: Section, i) => (
              <>
                <p>{s.name}</p>
                <TableContainer key={i}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {s.headers.map((v, i) => (
                          <TableCell key={i}>{v}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {s.rows.map((row, index) =>
                        typeof row === "object" ? (
                          <TableRow key={index}>
                            {Object.values(row).map((v: any, i) => (
                              <TableCell key={i}>
                                {
                                  <AnnotationText
                                    api={initAnnotations(
                                      (extractValue(v, "0") || "").toString(),
                                      section,
                                      bundleKey,
                                      feedback,
                                      onSave,
                                      index,
                                      i
                                    )}
                                    tag={section}
                                  />
                                }
                              </TableCell>
                            ))}
                          </TableRow>
                        ) : (
                          <AnnotationText
                            api={initAnnotations(
                              (extractValue(row, "0") || "").toString(),
                              section,
                              bundleKey,
                              feedback,
                              onSave,
                              index,
                              0
                            )}
                            tag={section}
                          />
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ))}
          </ErrorBoundary>
        );
      default:
        return <>{content}</>;
    }
  }
  let out = <>{content}</>;
  try {
    out = sectionComponent();
  } catch (e) {
    console.info("error", e);
  }
  return out;
}
